<template>
	<article
		:id="containerId"
		:class="cssClass"
		:data-banner-type="bannerType"
	/>
</template>

<script setup lang="ts">
import { onMounted, watch, computed, nextTick } from 'vue';
import { useAdManager } from '../composables/useAdManager';
import { useSearchStore } from '@stores/useSearchStore';
import { AdTargetingKey } from '../types/google-tag.types';
import type { BannerType, IDefinedSlot } from '../types/google-tag.types';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { useRollbar } from '@composables/useRollbar';
import { attachSlotRenderEnded } from '../services/slotRenderEnded';

const props = withDefaults(defineProps<{
	containerId: string;
	bannerType: BannerType;
	cssClass: string;
	clusterIndex?: number;
}>(), {
	clusterIndex: 0,
});

const { provider } = useAdManager();
const searchStore = useSearchStore();
const { logError } = useRollbar();

const selectedDiscipline = computed<string | undefined>(() => {
	const disciplineFilter = searchStore.filtersSelection.find(
		(f) => f.type === FilterType.DISCIPLINES,
	);
	return disciplineFilter?.values?.[0]?.id;
});

const handleBannerLoading = (): void => {
	const slots: IDefinedSlot[] = window.adSlots?.[props.bannerType] ?? [];
	if (slots.length === 0) return;

	onRightBannerProceedWithLoading(slots);
};

const onRightBannerProceedWithLoading = (slots: IDefinedSlot[]): void => {
	const isLastSlotInList = slots.find(
		(slot, index) => slot.getSlotElementId() === props.containerId && index === slots.length - 1,
	) !== undefined;

	if (!isLastSlotInList) return;

	clearBannerTargetingIfSet();
	useSelectedDisciplineForBannerTargeting();
	provider.refreshSlots(slots);
};

const useSelectedDisciplineForBannerTargeting = (): void => {
	if (!selectedDiscipline.value) return;

	const targetedDiscipline = getTargetedDiscipline();
	if (targetedDiscipline && targetedDiscipline === selectedDiscipline.value) return;

	provider.setTargeting(AdTargetingKey.DISCIPLINE, selectedDiscipline.value);
};

const clearBannerTargetingIfSet = (): void => {
	const targetedDiscipline = getTargetedDiscipline();
	if (!targetedDiscipline) return;

	provider.clearTargeting(AdTargetingKey.DISCIPLINE);
};

const getTargetedDiscipline = (): string | false => {
	const currentTargeting = provider.getTargeting(AdTargetingKey.DISCIPLINE);
	if (!currentTargeting?.length) return false;

	return currentTargeting[0] ?? false;
};

const displayBannerSlot = (): void => {
	provider.cmdPush(() => provider.displayContainer(props.containerId));
};

const attachSlotRenderEndedListener = (): void => {
	const pubads = window.googletag?.pubads?.();
	attachSlotRenderEnded(pubads, logError);
};

const initBanner = async (): Promise<void> => {
	if (!provider.isReady()) {
		document.addEventListener('gam_ready', () => void initBanner());
		return;
	}

	attachSlotRenderEndedListener();

	displayBannerSlot();
	await nextTick(handleBannerLoading);
};

onMounted(async () => {
	if (!import.meta.client) return;

	// Guard: OmniSearch running without Portal (standalone / unit tests)
	if (typeof window.googletag === 'undefined') return;

	await initBanner();
});

if (import.meta.client) {
	watch(selectedDiscipline, handleBannerLoading);
}
</script>
